import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useCheckActiveAppointments,
  useCreateOnSiteAppointmentMutation,
} from '../../api/appointments.queries'
import { useBuildingsSearch, useLeadsSearch, type Building, type Lead } from '../../api/leads.api'

import { BUILDINGS_PAGE_SIZE, LEADS_PAGE_SIZE } from './constants.ts'
import Option from './Option'

interface CreateOSADialogProps {
  open: boolean
  onClose: () => void
}

export const CreateOSADialog = ({ open, onClose }: CreateOSADialogProps) => {
  const navigate = useNavigate()
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null)
  const [appointmentType, setAppointmentType] = useState<'isfp' | 'marketplace'>('isfp')
  // Only meaningful (and shown) when appointmentType is 'marketplace' —
  // which product(s) the appointment covers. A scout can select either
  // product or both at once, same as the scout-app's own picker.
  const [selectedSubtypes, setSelectedSubtypes] = useState<('heatpump' | 'pv')[]>([])
  const [leadSearchQuery, setLeadSearchQuery] = useState('')
  const [leadInputValue, setLeadInputValue] = useState('')
  const [leadOffset, setLeadOffset] = useState(0)
  const [allLeads, setAllLeads] = useState<Lead[]>([])
  const [hasMoreLeads, setHasMoreLeads] = useState(true)
  const [isLoadingMoreLeads, setIsLoadingMoreLeads] = useState(false)
  const leadListboxRef = useRef<HTMLUListElement>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Building pagination state
  const [buildingOffset, setBuildingOffset] = useState(0)
  const [allBuildings, setAllBuildings] = useState<Building[]>([])
  const [hasMoreBuildings, setHasMoreBuildings] = useState(true)
  const [isLoadingMoreBuildings, setIsLoadingMoreBuildings] = useState(false)

  // Fetch leads from API with pagination
  const { data: leadsData, isLoading: loadingLeads } = useLeadsSearch({
    q: leadSearchQuery,
    limit: LEADS_PAGE_SIZE,
    offset: leadOffset,
    enabled: open && leadSearchQuery.length > 0,
  })

  // Fetch buildings from API with pagination
  const { data: buildingsData, isLoading: loadingBuildings } = useBuildingsSearch({
    leadUuid: selectedLead?.user_uuid,
    limit: BUILDINGS_PAGE_SIZE,
    offset: buildingOffset,
    enabled: open && !!selectedLead, // Load immediately when lead is selected
  })

  // Check for active appointments for the selected building
  const { data: activeAppointment, isLoading: isCheckingActiveAppointment } =
    useCheckActiveAppointments(selectedBuilding?.uuid, appointmentType, {
      enabled: !!selectedBuilding?.uuid,
    })

  const hasActiveAppointment = !!activeAppointment

  // Validate required fields and return missing fields
  const validateLeadData = (lead: Lead): string[] => {
    const missingFields: string[] = []
    if (!lead.user_uuid) missingFields.push('User ID')
    if (!lead.first_name) missingFields.push('First Name')
    if (!lead.last_name) missingFields.push('Last Name')
    if (!lead.email) missingFields.push('Email')
    if (!lead.phone) missingFields.push('Phone')
    return missingFields
  }

  const validateBuildingData = (building: Building): string[] => {
    const missingFields: string[] = []
    if (!building.uuid) missingFields.push('Building ID')
    if (!building.address) missingFields.push('Street Address')
    if (!building.city) missingFields.push('City')
    if (!building.postal_code) missingFields.push('Postal Code')
    return missingFields
  }

  // Create appointment mutation
  const createAppointmentMutation = useCreateOnSiteAppointmentMutation({
    onSuccess: result => {
      // Navigate to the created appointment
      navigate(`/osa-scheduling/show/${result.data.uuid}`)
      onClose()
    },
    onError: error => {
      console.error('Failed to create appointment:', error)
      setSubmitError(
        error instanceof Error ? error.message : 'Failed to create appointment. Please try again.'
      )
    },
  })

  // Update leads list when new data arrives
  useEffect(() => {
    if (leadsData) {
      if (leadOffset === 0) {
        // First page - replace all leads
        setAllLeads(leadsData.data)
      } else {
        // Subsequent pages - append to existing leads
        setAllLeads(prev => [...prev, ...leadsData.data])
      }
      // Check if there are more leads to load
      setHasMoreLeads(
        leadsData.data.length === LEADS_PAGE_SIZE && leadOffset + LEADS_PAGE_SIZE < leadsData.count
      )
      setIsLoadingMoreLeads(false)
    }
  }, [leadsData, leadOffset])

  // Update buildings list when new data arrives
  useEffect(() => {
    if (buildingsData) {
      if (buildingOffset === 0) {
        // First page - replace all buildings
        setAllBuildings(buildingsData.data)
      } else {
        // Subsequent pages - append to existing buildings
        setAllBuildings(prev => [...prev, ...buildingsData.data])
      }
      // Check if there are more buildings to load
      setHasMoreBuildings(
        buildingsData.data.length === BUILDINGS_PAGE_SIZE &&
          buildingOffset + BUILDINGS_PAGE_SIZE < buildingsData.count
      )
      setIsLoadingMoreBuildings(false)
    }
  }, [buildingsData, buildingOffset])

  // Reset building selection and pagination when lead changes
  useEffect(() => {
    setSelectedBuilding(null)
    setBuildingOffset(0)
    setAllBuildings([])
    setHasMoreBuildings(true)
    setValidationError(null) // Clear validation error when lead changes
  }, [selectedLead])

  // Validate when building changes
  useEffect(() => {
    if (selectedBuilding && selectedLead) {
      // Clear previous error
      setValidationError(null)

      // Skip validation if an OSA already exists
      if (hasActiveAppointment) {
        return
      }

      // Validate lead data
      const leadMissingFields = validateLeadData(selectedLead)
      if (leadMissingFields.length > 0) {
        setValidationError(
          `Lead information is incomplete. Missing fields: ${leadMissingFields.join(', ')}. Please contact support.`
        )
        return
      }

      // Validate building data
      const buildingMissingFields = validateBuildingData(selectedBuilding)
      if (buildingMissingFields.length > 0) {
        setValidationError(
          `Building information is incomplete. Missing fields: ${buildingMissingFields.join(', ')}. Please contact support.`
        )
      }
    } else if (selectedBuilding || selectedLead) {
      // Clear error if only one is selected
      setValidationError(null)
    }
  }, [selectedBuilding, selectedLead, hasActiveAppointment])

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedLead(null)
      setSelectedBuilding(null)
      setAppointmentType('isfp')
      setSelectedSubtypes([])
      setLeadSearchQuery('')
      setLeadInputValue('')
      setLeadOffset(0)
      setAllLeads([])
      setHasMoreLeads(true)
      setBuildingOffset(0)
      setAllBuildings([])
      setHasMoreBuildings(true)
      setValidationError(null)
    }
  }, [open])

  // Debounce lead search and reset pagination
  useEffect(() => {
    // Skip when the input hasn't changed (e.g. on mount), so the list
    // isn't cleared while the user is interacting with it
    if (leadInputValue === leadSearchQuery) {
      return undefined
    }

    const timer = setTimeout(() => {
      setLeadSearchQuery(leadInputValue)
      setLeadOffset(0) // Reset pagination on new search
      setAllLeads([]) // Clear leads on new search
      setHasMoreLeads(true)
    }, 300)

    return () => clearTimeout(timer)
  }, [leadInputValue, leadSearchQuery])

  // Handle scroll to load more leads
  const handleLeadListboxScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget as HTMLUListElement

      // Check if scrolled to bottom
      if (
        listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10 &&
        hasMoreLeads &&
        !isLoadingMoreLeads &&
        !loadingLeads
      ) {
        setIsLoadingMoreLeads(true)
        setLeadOffset(prev => prev + LEADS_PAGE_SIZE)
      }
    },
    [hasMoreLeads, isLoadingMoreLeads, loadingLeads]
  )

  // Handle scroll to load more buildings in dropdown
  const handleBuildingScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const menuNode = event.currentTarget as HTMLUListElement

      // Check if scrolled to bottom
      if (
        menuNode.scrollTop + menuNode.clientHeight >= menuNode.scrollHeight - 10 &&
        hasMoreBuildings &&
        !isLoadingMoreBuildings &&
        !loadingBuildings
      ) {
        setIsLoadingMoreBuildings(true)
        setBuildingOffset(prev => prev + BUILDINGS_PAGE_SIZE)
      }
    },
    [hasMoreBuildings, isLoadingMoreBuildings, loadingBuildings]
  )

  const handleAction = () => {
    if (!selectedLead || !selectedBuilding) return

    // Clear previous errors
    setValidationError(null)
    setSubmitError(null)

    if (hasActiveAppointment && activeAppointment) {
      // Navigate to appointment details - no validation needed
      navigate(`/osa-scheduling/show/${activeAppointment.uuid}`)
      onClose()
    } else {
      // Validate lead data
      const leadMissingFields = validateLeadData(selectedLead)
      if (leadMissingFields.length > 0) {
        setValidationError(
          `Lead information is incomplete. Missing fields: ${leadMissingFields.join(', ')}. Please contact support.`
        )
        return
      }

      // Validate building data
      const buildingMissingFields = validateBuildingData(selectedBuilding)
      if (buildingMissingFields.length > 0) {
        setValidationError(
          `Building information is incomplete. Missing fields: ${buildingMissingFields.join(', ')}. Please contact support.`
        )
        return
      }

      // Create new appointment
      createAppointmentMutation.mutate({
        leadUUID: selectedLead.user_uuid,
        firstName: selectedLead.first_name,
        lastName: selectedLead.last_name,
        email: selectedLead.email,
        phone: selectedLead.phone,
        buildingUUID: selectedBuilding.uuid,
        street: selectedBuilding.address,
        city: selectedBuilding.city,
        postalCode: selectedBuilding.postal_code,
        type: appointmentType,
        subtypes: appointmentType === 'marketplace' ? selectedSubtypes : undefined,
      })
    }
  }

  const toggleSubtype = (subtype: 'heatpump' | 'pv') => {
    setSelectedSubtypes(prev =>
      prev.includes(subtype) ? prev.filter(s => s !== subtype) : [...prev, subtype]
    )
  }

  const isSubmitDisabled =
    !selectedLead ||
    !selectedBuilding ||
    isCheckingActiveAppointment ||
    createAppointmentMutation.isPending ||
    !!validationError ||
    (appointmentType === 'marketplace' && selectedSubtypes.length === 0)

  const getButtonText = () => {
    if (isCheckingActiveAppointment) return 'Checking...'
    if (createAppointmentMutation.isPending) return 'Creating...'
    if (hasActiveAppointment) return 'Open'
    return 'Create'
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create On-Site Appointment</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Lead Search Autocomplete */}
          <Autocomplete
            options={Array.isArray(allLeads) ? allLeads : []}
            value={selectedLead}
            onChange={(_, newValue) => setSelectedLead(newValue)}
            inputValue={leadInputValue}
            onInputChange={(_, newInputValue) => setLeadInputValue(newInputValue)}
            getOptionLabel={option => `${option.full_name} (${option.phone || 'no phone'})`}
            isOptionEqualToValue={(option, value) => option.user_uuid === value.user_uuid}
            loading={loadingLeads && leadOffset === 0}
            loadingText="Loading leads..."
            noOptionsText={
              leadInputValue.length === 0 ? 'Start typing to search leads...' : 'No leads found'
            }
            filterOptions={options => {
              // Return options as-is since we're using API search
              return Array.isArray(options) ? options : []
            }}
            slotProps={{
              listbox: {
                onScroll: handleLeadListboxScroll,
                ref: leadListboxRef,
                style: { maxHeight: 250 },
              },
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Search Lead"
                placeholder="Type lead name or email..."
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {(loadingLeads || isLoadingMoreLeads) && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Option {...props} key={option.user_uuid} option={option} />
            )}
            fullWidth
          />

          {/* Building Dropdown */}
          <FormControl fullWidth disabled={!selectedLead}>
            {selectedLead && allBuildings.length > 0 && (
              <InputLabel id="building-select-label">Building</InputLabel>
            )}
            <Select
              labelId="building-select-label"
              value={selectedBuilding?.uuid || ''}
              onChange={event => {
                const buildingUuid = event.target.value
                const building = allBuildings.find(b => b.uuid === buildingUuid)
                setSelectedBuilding(building || null)
              }}
              label={selectedLead && allBuildings.length > 0 ? 'Building' : ''}
              displayEmpty
              endAdornment={
                (loadingBuildings || isLoadingMoreBuildings) && (
                  <InputAdornment position="end" sx={{ pr: '20px' }}>
                    <CircularProgress size={20} />
                  </InputAdornment>
                )
              }
              MenuProps={{
                PaperProps: {
                  onScroll: handleBuildingScroll,
                  style: { maxHeight: 250 },
                },
              }}
            >
              {!selectedLead && (
                <MenuItem value="" disabled>
                  Select a lead first
                </MenuItem>
              )}
              {selectedLead && allBuildings.length === 0 && !loadingBuildings && (
                <MenuItem value="" disabled>
                  No buildings found
                </MenuItem>
              )}
              {selectedLead && loadingBuildings && buildingOffset === 0 && (
                <MenuItem value="" disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading buildings...
                </MenuItem>
              )}
              {allBuildings.map(building => (
                <MenuItem key={building.uuid} value={building.uuid}>
                  {building.full_address}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Appointment Type */}
          <FormControl fullWidth>
            <InputLabel id="type-select-label">Type</InputLabel>
            <Select
              labelId="type-select-label"
              value={appointmentType}
              label="Type"
              onChange={({ target: { value } }) => {
                setAppointmentType(value)
                if (value !== 'marketplace') {
                  setSelectedSubtypes([])
                }
              }}
            >
              <MenuItem value="isfp">iSFP</MenuItem>
              <MenuItem value="marketplace">Marketplace</MenuItem>
            </Select>
          </FormControl>

          {/* Product(s) — only relevant for marketplace appointments. Pill
              chips rather than checkboxes, matching the scout-app's own
              "select all that apply" product picker. */}
          {appointmentType === 'marketplace' && (
            <FormControl fullWidth component="fieldset">
              <FormLabel component="legend">Product</FormLabel>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  label="Heatpump"
                  clickable
                  color={selectedSubtypes.includes('heatpump') ? 'primary' : 'default'}
                  variant={selectedSubtypes.includes('heatpump') ? 'filled' : 'outlined'}
                  onClick={() => toggleSubtype('heatpump')}
                  sx={{ fontSize: '1rem', height: 36, '& .MuiChip-label': { px: 1.5 } }}
                />
                <Chip
                  label="Photovoltaic"
                  clickable
                  color={selectedSubtypes.includes('pv') ? 'primary' : 'default'}
                  variant={selectedSubtypes.includes('pv') ? 'filled' : 'outlined'}
                  onClick={() => toggleSubtype('pv')}
                  sx={{ fontSize: '1rem', height: 36, '& .MuiChip-label': { px: 1.5 } }}
                />
              </Stack>
            </FormControl>
          )}

          {/* Warning about existing appointment */}
          {hasActiveAppointment && (
            <Alert severity="warning">
              An active appointment already exists for this building.
            </Alert>
          )}

          {/* Validation error */}
          {validationError && <Alert severity="error">{validationError}</Alert>}

          {/* Create appointment error */}
          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAction}
          disabled={isSubmitDisabled}
          color={hasActiveAppointment ? 'secondary' : 'primary'}
          startIcon={
            isCheckingActiveAppointment || createAppointmentMutation.isPending ? (
              <CircularProgress size={16} />
            ) : undefined
          }
        >
          {getButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
